<template>
	<!-- <div style="display: block; max-width: 100%; height: 100%"> -->
	<div
		ref="gridContainer"
		class="grid-container"
		:tabindex="disableContainerFocus ? undefined : 0"
		@keyup="_onKeyPressed"
		@scroll.passive.capture="_onScroll"
	>
		<div ref="gridInnerContainer" class="grid-inner">
			<div class="grid" :class="{'grid-filtered': filtersEnabled}">

				<!-- HEADER BAR -->
				<div v-if="showHeaders" ref="headers" class="grid-header-row">
					<div v-if="checkboxVisible" class="grid-header-cell grid-header-select">
						<slot name="selected">
							<t-checkbox
								:checked="allSelected"
								:indeterminate="someSelected"
								:disableTabbing="disableSelectCheckboxTabbing"
								@change="_onSelectAllClicked"
							/>
						</slot>
					</div>

					<template v-for="column in localColumns">
						<div
							:ref="`header-${column.field}`"
							:key="`header-${column.field}`"
							:class="_getColumnClass(column)"
							:style="[columnStyles[column.field], _getColumnHeaderAlignStyle(column)]"

							@click="_handleColumnClicked($event, column)"
							@mousedown.prevent.stop="_handleColumnMouseDown($event, column)"
						>
							<div style="display: inline-block; width: calc(100% - 8px); overflow:hidden">
								<span class="grid-header-cell-text">
									<span>{{ columnDisplayNames[column.field] }}</span>
									<span v-if="column.sortOrder" style="padding-left: 0.1em">
										{{ column.sortOrder }} {{ _getSortDirectionCharacter(column) }}
									</span>
								</span>

							</div>
							<span
								v-if="_getColumnDefinitionByName(column.field).resizable"
								:key="`header-${column.field}-resizer`"
								:ref="`resize-${column.field}`"
								:style="`height: ${rowHeight}; top: 0`"
								class="grid-header-cell-resize"
								@mousedown.stop.prevent="_onResize($event, column)"
							/>
						</div>
					</template>
				</div>

				<!-- FILTER BAR -->
				<div
					v-if="filtersEnabled"
					ref="filters"
					class="grid-header-row"
				>

					<div v-if="checkboxVisible" class="grid-header-cell grid-header-select grid-header-filter">
						<t-pill v-if="selectedCount > 0" size="sm">{{selectedCount}}</t-pill>
					</div>
					<template v-for="column in localColumns">
						<div
							:ref="`filter-${column.field}`"
							:key="`filter-${column.field}`"
							:class="_getColumnClass(column)"
							:style="columnStyles[column.field]"
							class="grid-header-cell grid-header-filter"
						>
							<div style="display: inline-block; width: 100%; overflow:hidden;">
								<t-filter
									:columnDefinition="_getColumnDefinitionByName(column.field)"
									@filter-created="_onFilterModified"
									@filter-removed="_onFilterRemoved"
								/>
							</div>
						</div>
					</template>
				</div>

				<template v-if="showRows">
					<virtual-list
						v-if="useRecycleScroller"
						ref="recycleScroller"
						:debounce="100"
						:size="rowHeight"
						:remain="remains"
						:style="`height: calc(100% - ${getScrollerHeight}px)`"
						:item="row"
						:itemcount="sortedItems.length"
						:itemprops="getItemProps"
					/>
					<div
						v-else
						ref="withoutRecycleScroller"
						class="grid-item-container"
						:class="{'grid-item-container-no-filters': !showFilters}"
					>
						<t-row
							v-for="(item, rowIndex) in sortedItems"
							:key="item[idKey]"
							:item="item"
							:columns="localColumns"
							:columnStyles="columnStyles"
							:columnDefinitions="localColumnDefinitions"
							:selected="!!selected[item[idKey]]"
							:focused="rowIndex === focusedIndex"
							:hovered="enableDrop && isDragging && Boolean(hovered[idKey] === item[idKey])"
							:rowHeight="rowHeight"
							:activeRowWarning="negotiateActiveRowWarning(item)"
							:checkboxVisible="checkboxVisible"
							:draggable="draggable"
							:disableSelectCheckboxTabbing="disableSelectCheckboxTabbing"

							@click="_onRowClicked($event, item)"
							@checkboxClicked="_onCheckboxClicked(item)"
							@dblclick="_onRowDoubleClicked(item)"
							@contextmenu="_onRowContextMenu($event)"
							@mouseenter="_onRowMouseEnter(item)"
							@mouseleave="_onRowMouseLeave(item)"
						>
							<template
								v-for="(_, slot) of $scopedSlots"
								v-slot:[slot]="scope"
							>
								<slot
									:name="slot"
									v-bind="{
										rowIndex,
										columnIndex: fieldToColumnIndexMap.get(slot),
										...scope
									}"
								/>
							</template>
						</t-row>
					</div>
				</template>
				<div v-show="resizingColumn" ref="resizePreview" style="position: fixed; height: 100%; width: 2px; background-color:gray; border-left: 500px solid transparent; border-right: 500px solid transparent; background-clip: content-box; left:0; top:0; cursor: ew-resize" />
			</div>
		</div>
		<div
			v-show="showFakeScroller && !noFakeScroller"
			ref="fakeScroller"
			:style="fakeScrollerStyle"
			class="grid-fake-scroller"
		>
			<div :style="fakeScrollerInnerStyle" />
		</div>
		<t-draggable v-if="draggable" :context="self" />
	</div>
	<!-- </div> -->
</template>

<script>
// import { RecycleScroller } from 'vue-virtual-scroller';
import { v4 as newUUID } from 'uuid';
import TRow from './TRow';
import TFilter from './TFilter';
import columnsMixin from './mixins/columns';
import resizableMixin from './mixins/resizable';
import movableMixin from './mixins/movable';
import sortableAndFilterableMixin from './mixins/sortableAndFilterable';
import selectableMixin from './mixins/selectable';
import contextmenuMixin from './mixins/contextmenu';
// import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import VirtualList from '@rikishi/vue-virtual-scroll-list';
import './scss/_grid.scss';

export default {
	name: 'TGrid',
	components: {
		VirtualList,
		TFilter,
		TRow,
	},
	mixins: [ columnsMixin, resizableMixin, sortableAndFilterableMixin, movableMixin, selectableMixin, contextmenuMixin ],
	props: {
		draggable: {
			type: Boolean,
			default: false
		},
		/**
		 * Height of each row in the grid.
		 */
		rowHeight: {
			type: Number,
			required: true
		},

		/**
		 * Max height of the grid
		 */
		maxHeight: Number,

		/**
		 * Array containing the items to display in the grid.
		 */
		items: Array,

		/**
		 * Which property is the unique ID for each item.
		 */
		idKey: {
			type: String,
			default: 'id'
		},

		/**
		 * Array of functions that receive each item in the grid, return true if item should be kept in the grid or false if it should be filtered.
		 */
		filters: Array,
		/**
		 * Show/Hide filter header row.
		 */
		showFilters: {
			type: Boolean,
			default: true
		},

		/**
		 * Number of items before recycleScroller is used (optimization)
		 */
		recycleScrollerMinimumItems: {
			type: Number,
			default: 50
		},

		/**
		 * Force grid to never use recycleScroller if set
		 */
		noRecycleScroller: {
			type: Boolean,
			default: false
		},

		/**
		 * Should fakse scroller be shown?
		 */
		noFakeScroller: {
			type: Boolean,
			default: false
		},
		/**
		 * Initial sort. Takes an array og sort objects Eks: [{field: 'date', direction: 'desc'}, {field: 'name', direction: 'asc'}]
		*/
		sort: {
			type: Array,
			default: () => []
		},

		enableDrop: {
			type: Boolean,
			default: false
		},

		/**
		 * If true, tabindex is not set on grid container.
		 * Good for tabbing right into the grid, but bad if you want to focus the grid and then controll focus with arrow keys.
		 */
		disableContainerFocus: {
			type: Boolean,
			default: false
		},
	},
	data() {
		return {
			id: newUUID(),
			showRows: false,
			showHeaders: false,
			gridWidth: 'auto',
			throttlingScroll: false,
			hovered: {},
			row: TRow
		};
	},

	computed: {
		self() {
			return this;
		},
		remains() {
			return Math.min(this.sortedItems.length, 25);
		},
		computedStyle() {
			return `
				max-width: ${this.gridWidth}px;
			`;
		},
		getScrollerHeight() {
			//(.grid-header-cell height)
			let height = 32;
			if (this.showFilters) {
				//(.grid-header-filter height)
				height += 38;
			}

			return height;
		},
		useRecycleScroller() {
			return !this.noRecycleScroller
				&& this.items
				&& this.items.length >= this.recycleScrollerMinimumItems;
		},
		fakeScrollerStyle() {
			let headerHeight = 32;
			if (this.showFilters)
				headerHeight += 38;
			return { height: `calc(100% - ${headerHeight}px`, top: `${headerHeight}px` };
		},
		fakeScrollerInnerStyle() {
			let height = this.sortedItems.length * this.rowHeight;
			return { height: `${height}px`, };
		},
		targetScroller() {
			if (this.useRecycleScroller && this.$refs.recycleScroller)
				return this.$refs.recycleScroller.$el;
			else
				return this.$refs.withoutRecycleScroller;
		},
		showFakeScroller() {
			let totalWidth = this.localColumns.reduce((p,c) => c.width + p, 0);
			if (this.showRows && this.$refs.gridContainer) {
				if (this.checkboxVisible) {
					totalWidth += 40;
				}
				return totalWidth + 10 > this.$refs.gridContainer.offsetWidth;
			}
			return false;
		},
		filtersEnabled() {
			return this.showFilters && this.showHeaders;
		},
		fieldToColumnIndexMap() {
			let map = new Map();

			this.localColumns.forEach(
				(column, index) => map.set(column.field, index)
			);

			return map;
		},
		isDragging() {
			return this.$store.getters['draggable/isDragging'];
		}
	},
	watch: {
		selected(newVal) {
			this.$refs['gridContainer'].style.userSelect = newVal && Object.values(newVal).length ? 'none' : '';
		}
	},
	beforeCreate() {
		// Freezing changes outside behavior, would need to clone to avoid unexpected surprising behavior
		// If you want to freeze the data, do it outside the grid before sending to prop
		//Object.freeze(this.$options.propsData.items); // might be an idea ?
	},
	mounted() {
		this.showRows = false;
		this._calculateAllWidths();
		window.addEventListener('keydown', this.onKeyDown, { capture: true });
	},
	beforeDestroy() {
		window.removeEventListener('keydown', this.onKeyDown, { capture: true });
	},
	methods: {
		negotiateActiveRowWarning(item) {
			if (item && item.activeRowWarning)
				return true;
			return false;
		},
		onKeyDown(event) {
			if (!this.$refs.filters) return;

			let { key, target, shiftKey } = event;

			let tabable = Array.from(
				this.$refs.filters.querySelectorAll('input, button')
			).filter( x => !x.disabled);
			let currentIndex = tabable.indexOf(target);
			if (currentIndex === -1) return;

			if (key === 'Enter') {
				if (shiftKey) {
					if (currentIndex > 0) {
						tabable[currentIndex - 1].focus();
					} else {
						tabable[tabable.length - 1].focus();
					}
				} else if (currentIndex >= 0 && currentIndex < tabable.length - 1) {
					tabable[currentIndex + 1].focus();
				} else {
					tabable[0].focus();
				}
				event.preventDefault();
				// Event needs to propagate if filter needs to close popover.
				//event.stopPropagation();
			}
		},
		getItemProps(index) {
			return {
				props: {
					item: this.sortedItems[index],
					columns: this.localColumns,
					columnStyles: this.columnStyles,
					activeRowWarning: this.negotiateActiveRowWarning(this.sortedItems[index]),
					columnDefinitions: this.localColumnDefinitions,
					selected: Boolean(this.selected[this.sortedItems[index][this.idKey]]),
					focused: index === this.focusedIndex,
					hovered: this.enableDrop && this.$store.getters['draggable/isDragging'] && Boolean(this.hovered[this.idKey] === this.sortedItems[index][this.idKey]),
					rowHeight: this.rowHeight,
					checkboxVisible: this.checkboxVisible,
					draggable: this.draggable,
					disableSelectCheckboxTabbing: this.disableSelectCheckboxTabbing
				},
				on: {
					click: event => this._onRowClicked(event, this.sortedItems[index]),
					checkboxClicked: () => this._onCheckboxClicked(this.sortedItems[index]),
					dblclick: () => this._onRowDoubleClicked(this.sortedItems[index]),
					mouseenter: () => this._onRowMouseEnter(this.sortedItems[index]),
					mouseleave: () => this._onRowMouseLeave(this.sortedItems[index]),
					contextmenu: event => this._onRowContextMenu(event)
				},
				scopedSlots: { ...this.$scopedSlots }
			};
		},
		// Column functions
		getCurrentColumns() {
			return this.localColumnDefinitions;
		},
		setColumns(columns) {
			return this._handleNewColumns(columns);
		},
		resetColumns() {
			this._handleNewColumns(undefined);
		},

		// Selection functions
		getSelectedItems() {
			return Object.values(this.selected);
		},
		getSelectedCount() {
			return Object.keys(this.selected).length;
		},
		resetSelectedRows() {
			this._resetSelectedRows();
		},
		getHoveredItem() {
			return this.hovered;
		},
		focusFirst() {
			this.focusedIndex = 0;
		},
		focusLast() {
			this.focusedIndex = this.filteredItems.length - 1;
		},
		getFocusedIndex() {
			return this.focusedIndex;
		},
		removeFocus() {
			this.focusedIndex = -1;
		},
		focusGrid() {
			this.$refs.gridContainer.focus();
		},

		// private functions
		_onScroll(e) {
			if (e.target !== this.targetScroller && e.target !== this.$refs.fakeScroller)
				return;

			if (this.throttlingScroll) return;

			window.requestAnimationFrame(() => {
				let { fakeScroller } = this.$refs;
				let source = e.target;
				let target = (e.target === this.targetScroller) ? fakeScroller : this.targetScroller;
				if (target.scrollTop !== source.scrollTop)
					target.scrollTop = source.scrollTop;

				window.requestAnimationFrame(() => {
					this.throttlingScroll = false;
				});
			});
			this.throttlingScroll = true;
		},
		_onResize(e, column) {
			let [el] = this.$refs[`header-${column.field}`];
			let { left } = el.getBoundingClientRect();
			this.$refs.resizePreview.style.left = `${e.pageX - 500}px`;
			this._startResizeColumn(left, column);
		},

		_handleColumnMouseDown(e, column) {
			this._startMoveColumn(e, column);
		},

		_transformDefinitions(definitions) {
			if (definitions && typeof definitions === 'object')
				definitions = Object.values(definitions);
			let result = {};
			for (let def of definitions) {
				result[def.field] = { ...this.defaultColumnDefinition, ...def };
			}
			return result;
		},

		_handleColumnClicked(e, column) {
			if (this.resizingColumn || this.movableMoving)
				return;
			if (e.shiftKey) {
				this._addColumnToSort(column);
			} else {
				this._setColumnToSort(column);
			}
		},

		_onFilterModified(filter) {
			for (let i = 0; i < this.localFilters.length; i++) {
				if (this.localFilters[i].columnName === filter.columnName) {
					this.$set(this.localFilters, i, filter);
					return;
				}
			}
			this.localFilters.push(filter);
		},
		_onFilterRemoved(columnName) {
			for (let i = 0; i < this.localFilters.length; i++) {
				if (this.localFilters[i].columnName === columnName)
					this.localFilters.splice(i,1);
			}
		},
		_onKeyPressed(event) {
			switch (event.key) {
			case 'Delete': this.$emit('delete'); break;
			case 'Enter': this.$emit('enter'); break;
			case 'Esc': // IE/Edge specific value
			case 'Escape': this.$emit('escape'); break;
			default: break;
			}
		},

		_getColumnDefinitionByName(name) {
			if (this.localColumnDefinitions[name])
				return this.localColumnDefinitions[name];

			console.error(`Missing definition for ${name}.`);
			return {};
		},
		_onRowMouseEnter(item) {
			if (!this.enableDrop) return;
			if (!this.$store.getters['draggable/isDragging']) return;
			this.hovered = item;
		},
		_onRowMouseLeave() {
			this.hovered = {};
		}
	},
};
</script>


<docs>

## Column Definition
<a name="Grid-ColumnDefinition"></a>
Column definitions contains the backing definition of each column used in the grid.

| property                    | type                               | required? | description                                                                                                                                                                                          |
|-----------------------------|------------------------------------|-----------|------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------|
| field                       | string                             | **Y**     | Contains the name of the column, used to identify each column. Must be unique                                                                                                                        |
| width                       | number                             | N         | Default width of the column in pixels. All columns without a width set will be expanded to take equal width until grid fills parent                                                                  |
| minWidth                    | number                             | N         | Minimum width of the column                                                                                                                                                                          |
| valueGetter                 | Function                           | N         | Function will receive the item for each row, and returns the value used for sorting, filtering and display (if displayValueGetter is not set).                                                       |
| displayValueGetter          | Function                           | N         | Function will receive the item for each row, and returns the value used for display (overrides valueGetter).                                                                                         |
| resizable                   | boolean                            | N         | Should column be resizable?                                                                                                                                                                          |
| sortable                    | boolean                            | N         | Should column be sortable?                                                                                                                                                                           |
| filterable                  | boolean                            | N         | Should column be filterable?                                                                                                                                                                         |
| locked                      | "left" or "right"                  | N         | Should column be locked to left or right side of table? Will override resizable.                                                                                                                     |
| comparableValueGetter       | Function                           | N         | Function returning a comparable value from item.                                                                                                                                                     |
| cellClassRules              | Object                             | N         | Each property of the object should be a function taking in an item and returning true or false. If true, property name will be added as class.                                                       |
| cellClass                   | String or Function                 | N         | Should either be an string with css class name in *kebab-case* or *camelCase*, or a function taking in an item and returning a class name                                                            |
| cellStyle                   | Object or Function                 | N         | Should either be an object with css styles in *kebab-case* or *camelCase*, or a function taking in an item and returning the same style of object                                                    |
| align                       | "left" or "center" or "right"      | N         | Sets the alignment of text in the column and column-header.                                                                                                                                          |
| customFilter                | [CustomFilter](#Grid-CustomFilter) | N         | Custom filter definition for column.                                                                                                                                                                 |
| headerFilterButtonOnly      | boolean                            | N         | Should header filter only display a button?                                                                                                                                                          |
| headerFilterInputWithButton | boolean                            | N         | Should header filter display a button with the input?                                                                                                                                                |
| customFilterComponent       | Component                          | N         | Custom component handling the display in the filter popover. Must have a method *getFilterFunc* which returns the same result type as a FilterFactory. Grid will append the apply and clear buttons. |

## Custom Filter
<a name="Grid-CustomFilter"></a>

| property    | type                            | required? | description                                                                                                                                                                                                                        |
|-------------|---------------------------------|-----------|------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------|
| operations  | [{id,label,requiredInputCount}] | N         | List of available compare operations                                                                                                                                                                                               |
| factoryFunc | Function                        | N\*       | Function that reads id from selected operation and returns a function returning true or false per item. See example below. \*If custom operations are not all covered by default filter factory for column type, this is required. |
```js static
export default function filterFactory(columnDef, op, ...compValues) {
	let compValue;
	switch(op) {
	case '>=':
		compValue = compValues[0];
		return (item) => item[columnDef.field] >= compValue;
	case '>':
		compValue = compValues[0];
		return (item) => item[columnDef.field] > compValue;
	}
}
```

```jsx
<t-grid
	:columnDefinitions="[{ field: 'lineNo', width: 50 }, { field: 'quantity', type: 'number', minWidth: 300 }]"
	:rowHeight="28"
	:items="[{ id: '1', lineNo: '1', quantity: 10 },{ id: '2', lineNo: '2', quantity: 20 },{ id: '3', lineNo: '3', quantity: 30 }]"
	style="height: 400px" />
```

```jsx
<t-grid
	:columnDefinitions="[{ field: 'selected', type: 'checkbox', width: 40 }, { field: 'lineNo', width: 50 }, { field: 'quantity', type: 'number', minWidth: 300 }, { field: 'percentOfMax', width: 150 }]"
	:rowHeight="28"
	:items="[{ id: '1', lineNo: '1', quantity: 10, percentOfMax: 0.33 },{ id: '2', lineNo: '2', quantity: 20, percentOfMax: 0.5 },{ id: '3', lineNo: '3', quantity: 30, percentOfMax: 1 }]"
	:columns="[{ field: 'selected', width: 50 },{ field: 'lineNo', width: 300 }]"
	:filters="[(item) => item.lineNo >= 2]"
	:recycleScrollerMinimumItems="0"
	style="height: 400px"
>
	<template v-slot:percentOfMax="{ value }">
		<t-pie-icon size="small" :value="value * 100" />
	</template>
</t-grid>
```

```jsx
new Vue({
	data() {
		return {
			columnDefinitions: [
				{ field: 'lineNo', width: 40, resizable: false, displayName: '#', locked: 'left' },
				{
					field: 'quantity',
					type: 'number',
					minWidth: 300,
					filterable: false,
					sortable: true,
					cellClassRules: {
						foo(item) {
							return item.quantity > 10;
						}
					}
				},
				{
					field: 'percentOfMax',
					width: 50,
					displayName: '%',
					cellStyle(item) {
						if(item.percentOfMax > 0.5)
							return { 'background-color': 'yellowgreen' };
					},
					valueGetter(item) {
						return item.percentOfMax * 100;
					},
					customFilterComponent: {
						render: (createElement) => {
							let self = this;
							return createElement('p', [
								'Only show >40%'
							]);
						},
						methods: {
							getFilterFunc() {
								return (item) => {
									return item.percentOfMax > 0.40;
								}
							}
						}
					}
				}
			],
			defaultColumnDefinition: {
				resizable: true,
			},
			rowHeight: 28,
			items: Object.freeze([
				{ id: '1', lineNo: '1', quantity: 10, percentOfMax: 0.33 },
				{ id: '2', lineNo: '1', quantity: 20, percentOfMax: 0.5 },
				{ id: '3', lineNo: '3', quantity: 30, percentOfMax: 1 }
			]),
		}
	},
	template: `
		<t-grid
			:defaultColumnDefinition="defaultColumnDefinition"
			:columnDefinitions="columnDefinitions"
			:rowHeight="rowHeight"
			:items="items"
			localStorageId="testGrid"
			style="height: 400px"
			selectable
		>
			<template v-slot:percentOfMax="{ value }">
				<t-pie-icon size="small" :value="value" />
			</template>
		</t-grid>
	`
})
```
</docs>
